import { useState } from 'react';
import { useRouter } from 'next/router';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { deleteApi } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import Button from '@/components/base/Button';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
// Components
import Modal from '@/components/base/Modal';

import { DELETE_DASHBOARD } from '@/routes/apiEndpoints';
import { DASHBOARD } from '@/routes/privateRoutes';

export const DeleteConfirmModal = ({
    dashboard,
    isOpen,
    toggle,
    onSuccess,
    successBanner = true
}) => {
    const { t } = useTranslation('commons');
    const router = useRouter();
    const [status, setStatus] = useState({
        isLoading: false,
        isSuccess: false,
        errorMessage: null
    });

    /**
     * Delete dashboard
     * @returns {Promise<void>}
     */
    const deleteDashboard = async () => {
        setStatus((prevState) => ({
            ...prevState,
            isLoading: true,
            errorMessage: null
        }));

        try {
            await deleteApi(
                injectEndpointUrlParams(DELETE_DASHBOARD, { dashboardId: dashboard.id })
            );

            if (!successBanner) {
                return onSuccess();
            }

            setStatus((prevState) => ({
                ...prevState,
                isSuccess: true,
                isLoading: false
            }));
            setTimeout(() => {
                router.push(DASHBOARD);
            }, 1000);
        } catch (e) {
            setStatus((prevState) => ({
                ...prevState,
                errorMessage: t('errors:error_occured_submit_data'),
                isLoading: false
            }));
        }
    };

    return (
        <Modal open={isOpen} onDismiss={toggle}>
            <Modal.Body>
                {status.isSuccess && successBanner ? (
                    <CompoundBanner type="success" className="mb-4">
                        <CompoundBanner.Icon />
                        <CompoundBanner.Content
                            title={t('success')}
                            message={t('dashboard:delete_dashboard_confirmation_success')}
                        />
                    </CompoundBanner>
                ) : null}
                {status.errorMessage ? (
                    <CompoundBanner className="mb-4">
                        <CompoundBanner.Icon />
                        <CompoundBanner.Content
                            title={t('errors:error')}
                            message={status.errorMessage}
                        />
                        <CompoundBanner.Close
                            onClose={() =>
                                setStatus((prevState) => ({ ...prevState, errorMessage: null }))
                            }
                        />
                    </CompoundBanner>
                ) : null}
                <Modal.Title>{t('dashboard:delete_dashboard_confirmation_title')}</Modal.Title>
                <Modal.Content className="pt-8 text-center text-sm text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('dashboard:delete_dashboard_confirmation_content')}
                </Modal.Content>
                <Modal.Footer className="flex justify-center gap-6 pb-12 pt-8">
                    <Button
                        className="w-1/2"
                        isLoading={status.isLoading}
                        onClick={deleteDashboard}>
                        {t('yes')}
                    </Button>
                    <Button className="w-1/2" variant="secondary" onClick={toggle}>
                        {t('no')}
                    </Button>
                </Modal.Footer>
            </Modal.Body>
        </Modal>
    );
};
